import { useEffect, useMemo, useState } from 'react'
import { getReportingStores } from '../pages/api/getReportingStores'
import { getReportingRegions } from '../pages/api/getReportingRegions'
import { getReportingHotelGroups } from '../pages/api/getReportingHotelGroups'
import { getReportingStoreReps, ReportingStoreRep } from '../pages/api/getReportingStoreReps'
import { useAuth } from '../context/AuthContext'

type StoreOption = {
  storeId: string
  storeName: string
  humpClubStoreCode: string
  region?: string
}

type UseReportingStoresResult = {
  stores: StoreOption[]
  regions: string[]
  hotelGroups: string[]
  storeReps: ReportingStoreRep[]
  selectedStoreCode: string
  setSelectedStoreCode: (code: string) => void
  selectedRegion: string
  setSelectedRegion: (region: string) => void
  selectedHotelGroup: string
  setSelectedHotelGroup: (hotelGroup: string) => void
  selectedStoreRepId: string
  setSelectedStoreRepId: (storeRepId: string) => void
  loadingStores: boolean
  loadingRegions: boolean
  loadingHotelGroups: boolean
  loadingStoreReps: boolean
  error: string | null
}

const SELECTED_STORE_KEY = 'reportingSelectedStoreCode'
const SELECTED_REGION_KEY = 'reportingSelectedRegion'
const SELECTED_HOTEL_GROUP_KEY = 'reportingSelectedHotelGroup'
const SELECTED_STORE_REP_KEY = 'reportingSelectedStoreRepId'

export const useReportingStores = (): UseReportingStoresResult => {
  const { payload, token, loading: authLoading, error: authError } = useAuth()
  const isAdmin = payload?.role === 'ADMIN' || payload?.role === 'HEAD_OFFICE'
  const isStoreRep =
    payload?.isStoreRepMilie === true ||
    payload?.isStoreRepCore === true ||
    payload?.isMultiStoreUser === true
  const [stores, setStores] = useState<StoreOption[]>([])
  const [regions, setRegions] = useState<string[]>([])
  const [hotelGroups, setHotelGroups] = useState<string[]>([])
  const [storeReps, setStoreReps] = useState<ReportingStoreRep[]>([])
  const [selectedStoreCode, setSelectedStoreCodeState] = useState('')
  const [selectedRegion, setSelectedRegionState] = useState('')
  const [selectedHotelGroup, setSelectedHotelGroupState] = useState('')
  const [selectedStoreRepId, setSelectedStoreRepIdState] = useState('')
  const [loadingStores, setLoadingStores] = useState(false)
  const [loadingRegions, setLoadingRegions] = useState(false)
  const [loadingHotelGroups, setLoadingHotelGroups] = useState(false)
  const [loadingStoreReps, setLoadingStoreReps] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setSelectedStoreCode = (code: string) => {
    setSelectedStoreCodeState(code)
    if (typeof window !== 'undefined') {
      if (code) {
        window.sessionStorage.setItem(SELECTED_STORE_KEY, code)
      } else {
        window.sessionStorage.removeItem(SELECTED_STORE_KEY)
      }
    }
  }

  const setSelectedRegion = (region: string) => {
    setSelectedRegionState(region)
    if (typeof window !== 'undefined') {
      if (region) {
        window.sessionStorage.setItem(SELECTED_REGION_KEY, region)
      } else {
        window.sessionStorage.removeItem(SELECTED_REGION_KEY)
      }
    }
  }

  const setSelectedHotelGroup = (hotelGroup: string) => {
    setSelectedHotelGroupState(hotelGroup)
    if (typeof window !== 'undefined') {
      if (hotelGroup) {
        window.sessionStorage.setItem(SELECTED_HOTEL_GROUP_KEY, hotelGroup)
      } else {
        window.sessionStorage.removeItem(SELECTED_HOTEL_GROUP_KEY)
      }
    }
  }

  const setSelectedStoreRepId = (storeRepId: string) => {
    setSelectedStoreRepIdState(storeRepId)
    if (typeof window !== 'undefined') {
      if (storeRepId) {
        window.sessionStorage.setItem(SELECTED_STORE_REP_KEY, storeRepId)
      } else {
        window.sessionStorage.removeItem(SELECTED_STORE_REP_KEY)
      }
    }
  }

  useEffect(() => {
    if ((!isAdmin && !isStoreRep) || !token || authLoading || authError) {
      return
    }
    let mounted = true

    const loadStores = async () => {
      setLoadingStores(true)
      setError(null)
      try {
        const data = await getReportingStores(token)
        if (!mounted) return
        const sortedStores = [...data].sort((a: StoreOption, b: StoreOption) =>
          a.storeName.localeCompare(b.storeName, undefined, { sensitivity: 'base' }),
        )
        setStores(sortedStores)
        const cached =
          typeof window !== 'undefined' ? window.sessionStorage.getItem(SELECTED_STORE_KEY) : null
        const defaultCode =
          cached && sortedStores.some((store: StoreOption) => store.humpClubStoreCode === cached)
            ? cached
            : sortedStores[0]?.humpClubStoreCode || ''
        setSelectedStoreCode(defaultCode)
      } catch (err) {
        if (!mounted) return
        setError(err instanceof Error ? err.message : 'Failed to load stores')
      } finally {
        if (mounted) {
          setLoadingStores(false)
        }
      }
    }

    const loadRegions = async () => {
      setLoadingRegions(true)
      setError(null)
      try {
        const regions = await getReportingRegions(token)
        if (!mounted) return
        setRegions(regions)
      } finally {
        if (mounted) {
          setLoadingRegions(false)
        }
      }
    }

    const loadHotelGroups = async () => {
      setLoadingHotelGroups(true)
      setError(null)
      try {
        const groups = await getReportingHotelGroups(token)
        if (!mounted) return
        const sortedGroups = [...groups].sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        )
        setHotelGroups(sortedGroups)
        const cached =
          typeof window !== 'undefined'
            ? window.sessionStorage.getItem(SELECTED_HOTEL_GROUP_KEY)
            : null
        const defaultGroup =
          sortedGroups.length === 0
            ? ''
            : cached && sortedGroups.includes(cached)
            ? cached
            : sortedGroups[0]
        setSelectedHotelGroup(defaultGroup)
      } catch (err) {
        if (!mounted) return
        setError(err instanceof Error ? err.message : 'Failed to load hotel groups')
      } finally {
        if (mounted) {
          setLoadingHotelGroups(false)
        }
      }
    }

    const loadStoreReps = async () => {
      if (!isAdmin) {
        return
      }
      setLoadingStoreReps(true)
      setError(null)
      try {
        const reps = await getReportingStoreReps(token)
        if (!mounted) return
        const sortedReps = [...reps].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        )
        setStoreReps(sortedReps)
        const cached =
          typeof window !== 'undefined'
            ? window.sessionStorage.getItem(SELECTED_STORE_REP_KEY)
            : null
        const defaultStoreRepId =
          cached && sortedReps.some(rep => rep.id === cached) ? cached : sortedReps[0]?.id || ''
        setSelectedStoreRepId(defaultStoreRepId)
      } catch (err) {
        if (!mounted) return
        setError(err instanceof Error ? err.message : 'Failed to load store reps')
      } finally {
        if (mounted) {
          setLoadingStoreReps(false)
        }
      }
    }

    loadStores()

    // Only load regions if ADMIN
    if (payload?.role === 'ADMIN') {
      loadRegions()
    }

    if (isAdmin) {
      loadHotelGroups()
      loadStoreReps()
    }
    return () => {
      mounted = false
    }
  }, [isAdmin, isStoreRep, token, authLoading, authError])

  return {
    stores: useMemo(() => (isAdmin || isStoreRep ? stores : []), [isAdmin, isStoreRep, stores]),
    regions: useMemo(() => (isAdmin || isStoreRep ? regions : []), [isAdmin, isStoreRep, regions]),
    hotelGroups: useMemo(() => (isAdmin ? hotelGroups : []), [isAdmin, hotelGroups]),
    storeReps: useMemo(() => (isAdmin ? storeReps : []), [isAdmin, storeReps]),
    selectedStoreCode: isAdmin || isStoreRep ? selectedStoreCode : '',
    setSelectedStoreCode,
    selectedRegion,
    setSelectedRegion,
    selectedHotelGroup: isAdmin ? selectedHotelGroup : '',
    setSelectedHotelGroup,
    selectedStoreRepId: isAdmin ? selectedStoreRepId : '',
    setSelectedStoreRepId,
    loadingStores,
    loadingRegions,
    loadingHotelGroups,
    loadingStoreReps,
    error,
  }
}
